import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const COLORS = ['#F44336', '#FF9800', '#4CAF50', '#2196F3', '#9C27B0'];

const ObjectivesPriorityChart = ({ data }) => {
  const { t } = useTranslation();

  const getPriorityLabel = (rank) => {
    const r = Number(rank);
    if (r === 1) return t('Dashboard.priority1');
    if (r === 2) return t('Dashboard.priority2');
    if (r === 3) return t('Dashboard.priority3');
    return t('Dashboard.priorityRank', { rank: r });
  };

  // Count objectives by priority rank (key by rank number)
  const priorityCounts = {};
  data.forEach(obj => {
    const rank = obj.priority_rank || 0;
    priorityCounts[rank] = (priorityCounts[rank] || 0) + 1;
  });

  const chartData = Object.keys(priorityCounts).map((rank, index) => ({
    name: getPriorityLabel(rank),
    value: priorityCounts[rank],
    color: COLORS[index % COLORS.length]
  }));

  const hasData = chartData.length > 0;

  const renderCustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '4px',
          padding: '10px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        }}>
          <p style={{ margin: 0, fontWeight: 'bold', color: '#000' }}>
            {`${data.name}: ${data.value} objective${data.value !== 1 ? 's' : ''}`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer height={300} width="100%">
      {hasData ? (
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            outerRadius={90}
            innerRadius={40}
            dataKey="value"
            animationDuration={800}
            animationEasing="ease-out"
            label={({ name, value, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
          >
            {chartData.map((entry, index) => (
              <Cell key={entry.name} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={renderCustomTooltip} />
          <Legend
            layout="horizontal"
            align="center"
            verticalAlign="bottom"
            iconType="circle"
            wrapperStyle={{ paddingTop: '20px' }}
            formatter={(value, entry) => {
              const dataItem = chartData.find(item => item.name === value);
              return `${value}: ${dataItem ? dataItem.value : 0}`;
            }}
          />
        </PieChart>
      ) : (
        <div style={{
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          color: '#888'
        }}>
          {t('Dashboard.noDataAvailable')}
        </div>
      )}
    </ResponsiveContainer>
  );
};

ObjectivesPriorityChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    priority_rank: PropTypes.number,
  })).isRequired,
};

export default ObjectivesPriorityChart;

